import { useState } from 'react';
import { History, X } from 'lucide-react';
import { getVisibleRecentPages, useNavigationStore } from '../../stores/navigationStore';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface NavigationRecentsProps {
  currentUserId: string | null;
  allowedPaths: ReadonlySet<string>;
}

export function NavigationRecents({ currentUserId, allowedPaths }: NavigationRecentsProps) {
  const { recentPages, recentPagesOwnerId, clearRecentPages, density } = useNavigationStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleRecentPages = getVisibleRecentPages(
    recentPages,
    recentPagesOwnerId,
    currentUserId,
    allowedPaths,
  );

  if (visibleRecentPages.length === 0) {
    return null;
  }

  const paddingClass = {
    compact: 'py-1.5 px-2',
    normal: 'py-2 px-3',
    comfortable: 'py-2.5 px-3',
  }[density];

  const spacingClass = {
    compact: 'space-y-0.5',
    normal: 'space-y-1',
    comfortable: 'space-y-1.5',
  }[density];

  // Group by time
  const now = Date.now();
  const today: typeof visibleRecentPages = [];
  const yesterday: typeof visibleRecentPages = [];
  const earlier: typeof visibleRecentPages = [];

  visibleRecentPages.forEach((page) => {
    const hoursDiff = (now - page.timestamp) / (1000 * 60 * 60);
    if (hoursDiff < 24) {
      today.push(page);
    } else if (hoursDiff < 48) {
      yesterday.push(page);
    } else {
      earlier.push(page);
    }
  });

  const renderPages = (pages: typeof visibleRecentPages) => {
    return pages.map((page) => (
      <Link
        key={page.id}
        to={page.path}
        className={`w-full flex items-center justify-between ${paddingClass} text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition group`}
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {page.icon && <span className="text-gray-400">{page.icon}</span>}
          <span className="truncate">{page.name}</span>
        </div>
        <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
          {formatDistanceToNow(page.timestamp, { addSuffix: true })}
        </span>
      </Link>
    ));
  };

  return (
    <div className={spacingClass}>
      <div className="px-3 py-2 flex items-center justify-between">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-2 rounded text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          aria-expanded={isExpanded}
          aria-controls="sidebar-recent-pages"
        >
          <History className="w-3.5 h-3.5" />
          <span>Recent</span>
          <span className="text-gray-400">({visibleRecentPages.length})</span>
        </button>
        {visibleRecentPages.length > 0 && (
          <button
            onClick={clearRecentPages}
            className="p-1 text-gray-400 hover:text-red-600 rounded transition"
            title="Clear recent pages"
            aria-label="Clear recent pages"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {isExpanded && (
        <div id="sidebar-recent-pages" className={spacingClass}>
          {today.length > 0 && (
            <div className={spacingClass}>
              <div className="px-3 text-xs font-medium text-gray-400">Today</div>
              {renderPages(today)}
            </div>
          )}
          {yesterday.length > 0 && (
            <div className={spacingClass}>
              <div className="px-3 text-xs font-medium text-gray-400">Yesterday</div>
              {renderPages(yesterday)}
            </div>
          )}
          {earlier.length > 0 && (
            <div className={spacingClass}>
              <div className="px-3 text-xs font-medium text-gray-400">Earlier</div>
              {renderPages(earlier)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}




























